/**
 * Public-site SEO constants for Treydora.com (storefront only).
 * REACT_APP_SITE_ORIGIN optional override for staging (defaults to production origin).
 */

import { LEGAL_DOCUMENTS } from "../legal/legalRegistry";
import { LEGAL_PATHS } from "../legal/legalPaths";
import {
  SITE_HOME_PATH,
  SITE_MARKETPLACE_PATH,
  SITE_OSHAR_REAL_ESTATE_PATH,
  SITE_PUBLIC_LIBRARY_PATH,
  SITE_SUPPORT_PATH,
  SITE_WORLD_PATH,
} from "./siteNavRoutes";

export const SITE_ORIGIN = String(
  process.env.REACT_APP_SITE_ORIGIN || "https://treydora.com"
)
  .trim()
  .replace(/\/+$/, "");

export const SITE_NAME = "Treydora";
export const SITE_ORG_NAME = "Treydora Productions";

export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/images/world/oshar/oshar-hero.png`;
export const DEFAULT_OG_IMAGE_LIBRARY = `${SITE_ORIGIN}/assets/library/01_library_hero_great_hall.png`;
export const DEFAULT_OG_IMAGE_TRDX = `${SITE_ORIGIN}/assets/treydorex-sales/03_trdx_coin_primary.png`;

/** Paths that must never appear in sitemap and should carry noindex. */
export const NOINDEX_PATH_PREFIXES = [
  "/dashboard",
  "/library/read",
  "/library/success",
  "/library/my",
  "/login",
  "/signup",
  "/forgot-password",
  "/world/oshar/real-estate/success",
  "/profile",
];

export function absoluteSiteUrl(path = "/") {
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_ORIGIN}${p}`;
}

export function isNoindexPath(pathname = "") {
  const p = String(pathname || "");
  return NOINDEX_PATH_PREFIXES.some(
    (prefix) => p === prefix || p.startsWith(`${prefix}/`)
  );
}

export function buildCanonical(pathname = "/") {
  return absoluteSiteUrl(pathname.split("#")[0] || "/");
}

export const ORGANIZATION_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_ORG_NAME,
  url: SITE_ORIGIN,
  logo: `${SITE_ORIGIN}/favicon.png`,
  sameAs: [],
};

export const WEBSITE_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_ORIGIN,
  description:
    "Treydora is a dark sci-fi fantasy game universe — novel and NFT comic lore, TRDX utility access, Oshar worldbuilding, and a player marketplace preview.",
  publisher: { "@type": "Organization", name: SITE_ORG_NAME },
};

export const KORAXOS_BOOK_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Book",
  name: "Treydora: Rise of Koraxos",
  author: { "@type": "Organization", name: SITE_ORG_NAME },
  url: absoluteSiteUrl(SITE_PUBLIC_LIBRARY_PATH),
  description:
    "Origin novel for the Treydora dark sci-fi fantasy universe — the saga that anchors the game world, comic, and Oshar storyline.",
  inLanguage: "en",
  genre: ["Science Fiction", "Fantasy"],
};

export const GENESIS_COMIC_PRODUCT_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "Treydora Genesis NFT Comic",
  description:
    "Collectible on-chain comic edition with holder merchandising license terms. Story and art expand the Treydora universe — not a security or investment product.",
  brand: { "@type": "Brand", name: SITE_NAME },
  category: "Digital collectible · comic",
  url: absoluteSiteUrl(SITE_PUBLIC_LIBRARY_PATH),
};

/** Static public routes for sitemap.xml (no auth, dashboard, or reader). */
export const PUBLIC_SITEMAP_PATHS = [
  SITE_HOME_PATH,
  SITE_WORLD_PATH,
  `${SITE_WORLD_PATH}/oshar`,
  SITE_OSHAR_REAL_ESTATE_PATH,
  SITE_PUBLIC_LIBRARY_PATH,
  SITE_MARKETPLACE_PATH,
  "/chronicles",
  SITE_SUPPORT_PATH,
  LEGAL_PATHS.center,
  ...LEGAL_DOCUMENTS.map((d) => d.path),
  "/blog",
];

export const HOME_PAGE_SEO = {
  title: "Treydora | Dark Sci-Fi Fantasy Game Universe",
  description:
    "Enter Treydora — a dark sci-fi fantasy game universe born from the Rise of Koraxos novel and Genesis NFT comic. TRDX powers future game access; Oshar VR real estate and the marketplace are rolling out next.",
  ogTitle: "Treydora | Dark Sci-Fi Fantasy Game Universe",
  ogDescription:
    "Novel, NFT comic, TRDX utility token, and the world of Oshar — explore what is live now and what is coming soon on Treydora.com.",
  ogImage: DEFAULT_OG_IMAGE,
  canonicalUrl: absoluteSiteUrl(SITE_HOME_PATH),
  ogUrl: absoluteSiteUrl(SITE_HOME_PATH),
  jsonLd: [ORGANIZATION_JSON_LD, WEBSITE_JSON_LD, KORAXOS_BOOK_JSON_LD],
};

export const WORLD_OSHAR_SEO = {
  title: "World of Oshar | Treydora",
  description:
    "Explore Oshar — fractured kingdoms, arcane industry, and sky-spanning citadels in the Treydora dark sci-fi fantasy universe. VR districts and real-estate parcels are opening in phases.",
  ogImage: DEFAULT_OG_IMAGE,
  canonicalUrl: absoluteSiteUrl(SITE_WORLD_PATH),
  ogUrl: absoluteSiteUrl(SITE_WORLD_PATH),
};

export const OSHAR_REAL_ESTATE_SEO = {
  title: "Oshar VR Real Estate | Treydora",
  description:
    "Browse Oshar virtual property tiers for the Treydora game world — districts, parcels, and genesis releases. Virtual world access and collectibles; not real-world property or an investment offering.",
  ogImage: `${SITE_ORIGIN}/assets/dashboard/02_commercial_storefront_card.png`,
  canonicalUrl: absoluteSiteUrl(SITE_OSHAR_REAL_ESTATE_PATH),
  ogUrl: absoluteSiteUrl(SITE_OSHAR_REAL_ESTATE_PATH),
};

export const LIBRARY_LISTING_SEO = {
  title: "Treydora Library | Books & Genesis NFT Comic",
  description:
    "Read Treydora: Rise of Koraxos and collect the Genesis NFT comic — digital editions, holder perks, and archive releases from the official storefront.",
  ogTitle: "Treydora Library | Books & Genesis NFT Comic",
  ogDescription:
    "Official Treydora library — novel, Genesis NFT comic editions, and digital collectibles from the game universe.",
  ogImage: DEFAULT_OG_IMAGE_LIBRARY,
  canonicalUrl: absoluteSiteUrl(SITE_PUBLIC_LIBRARY_PATH),
  ogUrl: absoluteSiteUrl(SITE_PUBLIC_LIBRARY_PATH),
  jsonLd: [GENESIS_COMIC_PRODUCT_JSON_LD, KORAXOS_BOOK_JSON_LD],
};

export const MARKETPLACE_SEO = {
  title: "Treydora Marketplace Preview | Coming Soon",
  description:
    "Preview the Treydora marketplace — creator storefronts and Oshar-aligned commerce are in development. Browse the coming-soon catalog; purchases activate when the marketplace goes live.",
  ogImage: `${SITE_ORIGIN}/assets/dashboard/02_commercial_storefront_card.png`,
  canonicalUrl: absoluteSiteUrl(SITE_MARKETPLACE_PATH),
  ogUrl: absoluteSiteUrl(SITE_MARKETPLACE_PATH),
};

export const CHRONICLES_SEO = {
  title: "The Chronicles | Treydora Lore",
  description:
    "Read Treydora Chronicles — lore articles, world updates, and story transmissions from the Rise of Koraxos universe.",
  canonicalUrl: absoluteSiteUrl("/chronicles"),
  ogUrl: absoluteSiteUrl("/chronicles"),
};

export const SUPPORT_SEO = {
  title: "Support | Treydora",
  description:
    "Get help with Treydora library purchases, NFT comic editions, TRDX, Oshar VR real estate, wallets, and account access.",
  canonicalUrl: absoluteSiteUrl(SITE_SUPPORT_PATH),
  ogUrl: absoluteSiteUrl(SITE_SUPPORT_PATH),
};

export const LEGAL_CENTER_SEO = {
  title: "Legal Center | Treydora",
  description:
    "Treydora terms, privacy, digital asset disclosures, NFT comic license terms, VR real estate terms, and marketplace policies.",
  canonicalUrl: absoluteSiteUrl(LEGAL_PATHS.center),
  ogUrl: absoluteSiteUrl(LEGAL_PATHS.center),
};

export const BLOG_SEO = {
  title: "Treydora Blog | News & Updates",
  description: "News, dev updates, and articles from the Treydora Productions team.",
  canonicalUrl: absoluteSiteUrl("/blog"),
  ogUrl: absoluteSiteUrl("/blog"),
};

export const NOINDEX_PRIVATE_SEO = {
  robots: "noindex, nofollow",
};
